import { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import { View, Pressable, ScrollView, Alert, Modal, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { InputField } from '@/components/ui/InputField';
import { SelectField } from '@/components/ui/SelectField';
import { Calendar } from '@/components/ui/Calendar';
import { CityAutocomplete } from '@/components/ui/CityAutocomplete';
import { ThemedText, SubHeading, Body, Caption } from '@/components/ui/Text';
import { useTheme } from '@/theme';
import type { TripDestination } from '@/services/trips';
import { getDestinationCoordinates } from '@/services/weather';
import { toIso2CountryCode } from '@/utils/countryIso2';
import { tripDestinationsSnapshotKey } from '@/utils/tripDestination';
import { TripErrorBoundary } from '@/utils/errorBoundary';

type VerifyCandidate = {
  label: string;
  country: string;
  state?: string;
  latitude: number;
  longitude: number;
  locationId: string;
  placeId?: string;
};

type MultiCityDestinationFormProps = {
  destinations: TripDestination[];
  tripStartDate: Date;
  tripEndDate: Date;
  onChange: (destinations: TripDestination[]) => void;
  onClose?: () => void;
  /**
   * When true, destination date pickers are clamped to the tripStartDate/tripEndDate window.
   * For multi-city editing, trip dates are derived from destinations, so this should be false.
   */
  constrainToTripDates?: boolean;
};

function cloneDestinations(list: TripDestination[]): TripDestination[] {
  return list.map((dest) => ({
    ...dest,
    startDate: new Date(dest.startDate),
    endDate: new Date(dest.endDate),
    ...(dest.coordinates
      ? { coordinates: { ...dest.coordinates } }
      : {}),
  }));
}

function MultiCityDestinationFormInner({
  destinations: initialDestinations,
  tripStartDate,
  tripEndDate,
  onChange,
  onClose,
  constrainToTripDates = true,
}: MultiCityDestinationFormProps) {
  const theme = useTheme();
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const lastNotifiedKeyRef = useRef<string>('__pending__');
  const lastSyncedKeyRef = useRef<string>('__pending__');

  const [destinations, setDestinations] = useState<TripDestination[]>(() =>
    cloneDestinations(initialDestinations),
  );
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [showCalendar, setShowCalendar] = useState<{ index: number; type: 'start' | 'end' } | null>(null);
  const [verifyPicker, setVerifyPicker] = useState<{ index: number; candidates: VerifyCandidate[] } | null>(null);
  const [verifyingByIndex, setVerifyingByIndex] = useState<Record<number, boolean>>({});
  const verifyRequestTokenRef = useRef<Record<number, number>>({});

  const incomingSyncKey = useMemo(
    () => tripDestinationsSnapshotKey(initialDestinations),
    [initialDestinations],
  );

  const notifyParent = useCallback((next: TripDestination[]) => {
    const key = tripDestinationsSnapshotKey(next);
    if (key === lastNotifiedKeyRef.current) return;
    lastNotifiedKeyRef.current = key;
    lastSyncedKeyRef.current = key;
    onChangeRef.current(next);
  }, []);

  // Sync from parent only when destination *content* changes — not on new array refs.
  useLayoutEffect(() => {
    if (incomingSyncKey === lastSyncedKeyRef.current) return;
    lastSyncedKeyRef.current = incomingSyncKey;
    lastNotifiedKeyRef.current = incomingSyncKey;
    setDestinations(cloneDestinations(initialDestinations));
  }, [incomingSyncKey, initialDestinations]);

  // Initialize with single destination if empty
  useEffect(() => {
    if (destinations.length === 0 && initialDestinations.length === 0) {
      // Use trip dates if available, otherwise use future dates (not today)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      // Check if trip dates are valid (not epoch date 1970-01-01)
      const isValidDate = (date: Date) => {
        return date && date.getTime() > new Date('2000-01-01').getTime();
      };
      
      let defaultStart: Date;
      if (tripStartDate && isValidDate(tripStartDate)) {
        defaultStart = new Date(tripStartDate);
        defaultStart.setHours(0, 0, 0, 0);
      } else {
        // Default to tomorrow (future date) instead of today
        defaultStart = tomorrow;
      }
      
      let defaultEnd: Date;
      if (tripEndDate && isValidDate(tripEndDate) && tripEndDate > defaultStart) {
        defaultEnd = new Date(tripEndDate);
        defaultEnd.setHours(0, 0, 0, 0);
      } else {
        // Default to day after start (future date)
        defaultEnd = new Date(defaultStart);
        defaultEnd.setDate(defaultEnd.getDate() + 1);
      }
      
      const seeded = [{
        city: '',
        startDate: defaultStart,
        endDate: defaultEnd,
      }];
      setDestinations(seeded);
      notifyParent(seeded);
    }
    // Seed once on mount when both parent and local lists are empty.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only empty seed
  }, []);

  const handleAddDestination = () => {
    const lastDestination = destinations[destinations.length - 1];
    const newStartDate = lastDestination
      ? new Date(lastDestination.endDate.getTime() + 24 * 60 * 60 * 1000) // Day after last destination ends
      : tripStartDate;
    
    const newEndDate = new Date(newStartDate);
    newEndDate.setDate(newEndDate.getDate() + 1); // Default 1 day

    const next = [
      ...destinations,
      {
        city: '',
        startDate: constrainToTripDates && newStartDate > tripEndDate ? tripStartDate : newStartDate,
        endDate: constrainToTripDates && newEndDate > tripEndDate ? tripEndDate : newEndDate,
      },
    ];
    setDestinations(next);
    notifyParent(next);
    setEditingIndex(destinations.length);
  };

  const handleRemoveDestination = (index: number) => {
    if (destinations.length <= 1) {
      Alert.alert('Cannot Remove', 'A trip must have at least one destination.');
      return;
    }
    const newDestinations = destinations.filter((_, i) => i !== index);
    setDestinations(newDestinations);
    notifyParent(newDestinations);
  };

  const handleUpdateDestination = (index: number, updates: Partial<TripDestination>) => {
    const newDestinations = [...destinations];
    const currentDest = newDestinations[index];
    
    // Normalize dates (set to midnight for consistent comparison)
    const normalizeDate = (date: Date) => {
      const normalized = new Date(date);
      normalized.setHours(0, 0, 0, 0);
      return normalized;
    };
    
    // Process start date updates
    if (updates.startDate) {
      let newStartDate = normalizeDate(updates.startDate);
      
      // Only constrain to trip dates if they are valid
      if (constrainToTripDates && tripStartDate && tripStartDate > new Date(0)) {
        const normalizedTripStart = normalizeDate(tripStartDate);
        if (newStartDate < normalizedTripStart) {
          newStartDate = normalizedTripStart;
        }
      }
      if (constrainToTripDates && tripEndDate && tripEndDate > new Date(0)) {
        const normalizedTripEnd = normalizeDate(tripEndDate);
        if (newStartDate > normalizedTripEnd) {
          newStartDate = normalizedTripEnd;
        }
      }
      
      // Ensure start date is before end date
      if (currentDest.endDate) {
        const normalizedEnd = normalizeDate(currentDest.endDate);
        if (newStartDate >= normalizedEnd) {
          // Adjust end date to be at least 1 day after start
          const newEndDate = new Date(newStartDate);
          newEndDate.setDate(newEndDate.getDate() + 1);
          updates.endDate = newEndDate;
        }
      }
      
      updates.startDate = newStartDate;
    }
    
    // Process end date updates
    if (updates.endDate) {
      let newEndDate = normalizeDate(updates.endDate);
      
      // Only constrain to trip dates if they are valid
      if (constrainToTripDates && tripStartDate && tripStartDate > new Date(0)) {
        const normalizedTripStart = normalizeDate(tripStartDate);
        if (newEndDate < normalizedTripStart) {
          newEndDate = new Date(normalizedTripStart);
          newEndDate.setDate(newEndDate.getDate() + 1);
        }
      }
      if (constrainToTripDates && tripEndDate && tripEndDate > new Date(0)) {
        const normalizedTripEnd = normalizeDate(tripEndDate);
        if (newEndDate > normalizedTripEnd) {
          newEndDate = normalizedTripEnd;
        }
      }
      
      // Ensure end date is after start date
      const currentStart = updates.startDate || currentDest.startDate;
      if (currentStart) {
        const normalizedStart = normalizeDate(currentStart);
        if (newEndDate <= normalizedStart) {
          const adjustedEnd = new Date(normalizedStart);
          adjustedEnd.setDate(adjustedEnd.getDate() + 1);
          newEndDate = adjustedEnd;
        }
      }
      
      updates.endDate = newEndDate;
    }
    
    // Apply all updates
    newDestinations[index] = { ...currentDest, ...updates };
    
    setDestinations(newDestinations);
    notifyParent(newDestinations);
  };

  const applyVerifiedCandidate = (index: number, candidate: VerifyCandidate) => {
    // Keep the user's entered label (dest.city) unless they explicitly selected a new candidate.
    // We store verified fields separately.
    handleUpdateDestination(index, {
      country: candidate.country,
      countryCode: toIso2CountryCode(candidate.country),
      locationId: candidate.locationId,
      placeId: candidate.placeId ?? candidate.locationId,
      coordinates: {
        latitude: candidate.latitude,
        longitude: candidate.longitude,
      },
    });
  };

  const handleGeocodeDestination = async (index: number) => {
    if (verifyingByIndex[index]) {
      return;
    }
    const destination = destinations[index];
    if (!destination.city.trim()) {
      Alert.alert('Error', 'Please enter a city name first.');
      return;
    }

    const requestToken = (verifyRequestTokenRef.current[index] || 0) + 1;
    verifyRequestTokenRef.current[index] = requestToken;
    const isLatestRequest = () => verifyRequestTokenRef.current[index] === requestToken;
    setVerifyingByIndex((prev) => ({ ...prev, [index]: true }));

    try {
      // Use the Cloud Function directly to avoid Mapbox 403 errors
      const { httpsCallable, functions } = await import('@/lib/functions');
      const geocodeLocation = httpsCallable(functions, 'geocodeLocation');
      
      const geocodeResult = await geocodeLocation({ query: destination.city });

      const data = geocodeResult?.data as any;
      const candidatesRaw: any[] = Array.isArray(data?.results) ? data.results : [];
      const primaryRaw: any[] =
        typeof data?.lat === 'number' && typeof data?.lon === 'number' ? [data] : [];

      const candidates: VerifyCandidate[] = [...candidatesRaw, ...primaryRaw].flatMap((item) => {
          const name = item?.name || destination.city;
          const stateRaw = item?.state;
          const state = typeof stateRaw === 'string' ? stateRaw : undefined;
          const country = typeof item?.country === 'string' ? item.country : '';
          const latitude = item?.lat ?? item?.latitude;
          const longitude = item?.lon ?? item?.longitude;
          if (!country || typeof latitude !== 'number' || typeof longitude !== 'number') return [];
          const label = `${name}${state ? `, ${state}` : ''}, ${country}`;
          const locationId = `owm:${country}:${latitude.toFixed(5)},${longitude.toFixed(5)}`;
          const c: VerifyCandidate = {
            label,
            country,
            state,
            latitude,
            longitude,
            locationId,
            placeId: typeof item?.placeId === 'string' ? item.placeId : undefined,
          };
          return [c];
        });

      if (!isLatestRequest()) return;

      if (candidates.length === 0) {
        // Fallback to getDestinationCoordinates
        const coords = await getDestinationCoordinates(destination.city);
        if (!isLatestRequest()) return;
        if (coords) {
          const locationId = `fallback:${coords.country || 'unknown'}:${coords.latitude.toFixed(5)},${coords.longitude.toFixed(5)}`;
          applyVerifiedCandidate(index, {
            label: `${coords.city}${coords.country ? `, ${coords.country}` : ''}`,
            country: coords.country || '',
            latitude: coords.latitude,
            longitude: coords.longitude,
            locationId,
          });
          Alert.alert('Location verified', 'We verified this location to improve accuracy.');
          return;
        }
        Alert.alert('Not Found', `Could not verify "${destination.city}". You can still save without verifying.`);
        return;
      }

      if (candidates.length === 1) {
        // Single clear match: apply without changing the entered label
        applyVerifiedCandidate(index, candidates[0]);
        Alert.alert('Location verified', 'We verified this location to improve accuracy.');
        return;
      }

      // Multiple matches: let the user choose (prevents silent mis-match)
      setVerifyPicker({ index, candidates: candidates.slice(0, 8) });
    } catch (error) {
      if (!isLatestRequest()) return;
      console.error('[MultiCityDestinationForm] Geocoding error:', error);
      Alert.alert('Error', 'Failed to verify location. You can still save without verifying.');
    } finally {
      setVerifyingByIndex((prev) => {
        if (!isLatestRequest()) return prev;
        return { ...prev, [index]: false };
      });
    }
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
    });
  };

  return (
    <View style={{ gap: theme.spacing(2) }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
        <SubHeading>Destinations</SubHeading>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Add city"
          accessibilityHint="Adds another destination to your multi-city trip"
          onPress={handleAddDestination}
          style={({ pressed }) => ({
            flexDirection: 'row',
            alignItems: 'center',
            gap: theme.spacing(0.5),
            padding: theme.spacing(1),
            borderRadius: theme.radii.md,
            backgroundColor: theme.colors.primary + '20',
            opacity: pressed ? 0.7 : 1,
          })}
        >
          <Ionicons name="add" size={18} color={theme.colors.primary} />
          <ThemedText variant="bodyStrong" color={theme.colors.primary} style={{ fontSize: 14 }}>
            Add City
          </ThemedText>
        </Pressable>
      </View>

      <Caption color={theme.colors.textSecondary}>
        Add multiple cities with specific dates to get accurate weather forecasts for each location.
      </Caption>

      <ScrollView style={{ maxHeight: 400 }}>
        <View style={{ gap: theme.spacing(2) }}>
          {destinations.map((dest, index) => {
            const isVerifying = Boolean(verifyingByIndex[index]);
            return (
            <Card key={index} variant="outlined" style={{ padding: theme.spacing(2) }}>
              <View style={{ gap: theme.spacing(1.5) }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                  <ThemedText variant="bodyStrong" style={{ fontSize: 16 }}>
                    City {index + 1}
                  </ThemedText>
                  {destinations.length > 1 && (
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel={`Remove city ${index + 1}`}
                      accessibilityHint="Removes this destination from the trip"
                      onPress={() => handleRemoveDestination(index)}
                      style={({ pressed }) => ({
                        padding: theme.spacing(0.5),
                        opacity: pressed ? 0.7 : 1,
                      })}
                    >
                      <Ionicons name="trash-outline" size={20} color={theme.colors.error} />
                    </Pressable>
                  )}
                </View>

                <CityAutocomplete
                  label="City"
                  placeholder="e.g., Paris, France"
                  value={dest.city}
                  onChangeText={(text) => handleUpdateDestination(index, { city: text })}
                  onSelectCity={(suggestion) => {
                    const fallbackLocationId = suggestion.coordinates
                      ? `city:${suggestion.country || 'unknown'}:${suggestion.coordinates.latitude.toFixed(5)},${suggestion.coordinates.longitude.toFixed(5)}`
                      : undefined;
                    handleUpdateDestination(index, {
                      city: suggestion.fullName,
                      country: suggestion.country,
                      countryCode: suggestion.countryCode || toIso2CountryCode(suggestion.country),
                      placeId: suggestion.placeId,
                      locationId: suggestion.placeId || fallbackLocationId,
                      coordinates: suggestion.coordinates,
                    });
                  }}
                  allowUseAsEntered
                />

                {dest.country && (
                  <Body color={theme.colors.textSecondary} style={{ fontSize: 12 }}>
                    Country: {dest.country}
                  </Body>
                )}

                <View style={{ flexDirection: 'row', gap: theme.spacing(1.5) }}>
                  <View style={{ flex: 1 }}>
                    <ThemedText variant="body" style={{ fontSize: 12, marginBottom: theme.spacing(0.5) }}>
                      Arrival
                    </ThemedText>
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel={`Arrival date for city ${index + 1}, ${formatDate(dest.startDate)}. Tap to change date`}
                      accessibilityHint="Opens calendar picker to select arrival date"
                      onPress={() => setShowCalendar({ index, type: 'start' })}
                      style={({ pressed }) => ({
                        borderWidth: 1,
                        borderColor: theme.colors.border,
                        borderRadius: theme.radii.md,
                        padding: theme.spacing(1.5),
                        backgroundColor: theme.colors.surface,
                        opacity: pressed ? 0.7 : 1,
                        minHeight: 44,
                      })}
                    >
                      <ThemedText variant="body" style={{ fontSize: 14 }}>
                        {formatDate(dest.startDate)}
                      </ThemedText>
                    </Pressable>
                    <View style={{ marginTop: theme.spacing(1) }}>
                      <InputField
                        label="Arrival time (optional)"
                        placeholder="HH:mm"
                        value={dest.startTime || ''}
                        onChangeText={(text) => handleUpdateDestination(index, { startTime: text })}
                        keyboardType="numbers-and-punctuation"
                      />
                    </View>
                  </View>

                  <View style={{ flex: 1 }}>
                    <ThemedText variant="body" style={{ fontSize: 12, marginBottom: theme.spacing(0.5) }}>
                      Departure
                    </ThemedText>
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel={`Departure date for city ${index + 1}, ${formatDate(dest.endDate)}. Tap to change date`}
                      accessibilityHint="Opens calendar picker to select departure date"
                      onPress={() => setShowCalendar({ index, type: 'end' })}
                      style={({ pressed }) => ({
                        borderWidth: 1,
                        borderColor: theme.colors.border,
                        borderRadius: theme.radii.md,
                        padding: theme.spacing(1.5),
                        backgroundColor: theme.colors.surface,
                        opacity: pressed ? 0.7 : 1,
                        minHeight: 44,
                      })}
                    >
                      <ThemedText variant="body" style={{ fontSize: 14 }}>
                        {formatDate(dest.endDate)}
                      </ThemedText>
                    </Pressable>
                    <View style={{ marginTop: theme.spacing(1) }}>
                      <InputField
                        label="Departure time (optional)"
                        placeholder="HH:mm"
                        value={dest.endTime || ''}
                        onChangeText={(text) => handleUpdateDestination(index, { endTime: text })}
                        keyboardType="numbers-and-punctuation"
                      />
                    </View>
                  </View>
                </View>

                {dest.coordinates && (
                  <Caption color={theme.colors.success} style={{ fontSize: 11 }}>
                    ✓ Location verified ({dest.coordinates.latitude.toFixed(2)}, {dest.coordinates.longitude.toFixed(2)})
                  </Caption>
                )}

                <Button
                  label={isVerifying ? 'Verifying...' : (dest.coordinates ? 'Update Location (optional)' : 'Verify Location (optional)')}
                  variant="secondary"
                  onPress={() => handleGeocodeDestination(index)}
                  disabled={isVerifying}
                  icon={
                    isVerifying
                      ? <ActivityIndicator size="small" color={theme.colors.primary} />
                      : <Ionicons name="location" size={16} color={theme.colors.primary} />
                  }
                  style={{ marginTop: theme.spacing(0.5) }}
                />
              </View>
            </Card>
            );
          })}
        </View>
      </ScrollView>

      {/* Verify Location picker (when geocoding returns multiple matches) */}
      {verifyPicker && (
        <Modal
          visible={true}
          transparent
          animationType="fade"
          onRequestClose={() => setVerifyPicker(null)}
        >
          <Pressable
            onPress={() => setVerifyPicker(null)}
            style={{
              flex: 1,
              backgroundColor: 'rgba(0,0,0,0.5)',
              justifyContent: 'center',
              padding: theme.spacing(2),
            }}
          >
            <Pressable onPress={(e) => e.stopPropagation()}>
              <Card variant="outlined">
                <View style={{ gap: theme.spacing(1) }}>
                  <SubHeading>Confirm location</SubHeading>
                  <Caption color={theme.colors.textSecondary}>
                    We found multiple matches. Select the right one to verify (optional).
                  </Caption>

                  <ScrollView style={{ maxHeight: 320 }} keyboardShouldPersistTaps="handled">
                    <View style={{ gap: theme.spacing(0.5) }}>
                      {verifyPicker.candidates.map((candidate) => (
                        <Pressable
                          key={candidate.locationId}
                          onPress={() => {
                            applyVerifiedCandidate(verifyPicker.index, candidate);
                            setVerifyPicker(null);
                            Alert.alert('Location verified', `Verified as ${candidate.label}`);
                          }}
                          style={({ pressed }) => ({
                            padding: theme.spacing(1.5),
                            borderRadius: theme.radii.md,
                            backgroundColor: pressed ? theme.colors.borderMuted + '40' : 'transparent',
                            borderWidth: 1,
                            borderColor: theme.colors.borderMuted,
                          })}
                        >
                          <ThemedText variant="bodyStrong">{candidate.label}</ThemedText>
                          <Caption color={theme.colors.textSecondary}>
                            {candidate.latitude.toFixed(2)}, {candidate.longitude.toFixed(2)}
                          </Caption>
                        </Pressable>
                      ))}
                    </View>
                  </ScrollView>

                  <Button label="Skip" variant="ghost" onPress={() => setVerifyPicker(null)} />
                </View>
              </Card>
            </Pressable>
          </Pressable>
        </Modal>
      )}

      {/* Calendar Modal */}
      {showCalendar && (() => {
        const currentDest = destinations[showCalendar.index];
        const isStartDate = showCalendar.type === 'start';
        
        // Normalize helper
        const normalizeDate = (d: Date) => {
          const normalized = new Date(d);
          normalized.setHours(0, 0, 0, 0);
          return normalized;
        };
        
        // Calculate min/max dates - be more permissive
        let minDate: Date | undefined;
        let maxDate: Date | undefined;
        
        // Check if trip dates are valid (not epoch date)
        const hasValidTripStart = tripStartDate && tripStartDate > new Date(0);
        const hasValidTripEnd = tripEndDate && tripEndDate > new Date(0);
        
        const today = normalizeDate(new Date());
        
        // TEMPORARY: Remove all maxDate constraints to allow future date selection
        // TODO: Re-enable constraints once we verify date selection works
        if (isStartDate) {
          // For arrival date: Only set min if trip start is in the past
          // NO maxDate constraint - allow all future dates
          if (hasValidTripStart) {
            const normalizedTripStart = normalizeDate(tripStartDate);
            if (normalizedTripStart < today) {
              minDate = normalizedTripStart;
            }
          }
          // Don't set maxDate at all - allow unlimited future dates
        } else {
          // For departure date: Only set min based on start date
          // NO maxDate constraint - allow all future dates
          if (currentDest?.startDate) {
            const normalizedStart = normalizeDate(currentDest.startDate);
            const dayAfterStart = new Date(normalizedStart);
            dayAfterStart.setDate(dayAfterStart.getDate() + 1);
            dayAfterStart.setHours(0, 0, 0, 0);
            minDate = dayAfterStart;
          } else if (hasValidTripStart) {
            const normalizedTripStart = normalizeDate(tripStartDate);
            if (normalizedTripStart < today) {
              minDate = normalizedTripStart;
            }
          }
          // Don't set maxDate at all - allow unlimited future dates
        }
        
        // Debug logging
        console.log('[MultiCityDestinationForm] Calendar constraints:', {
          isStartDate,
          minDate: minDate?.toISOString(),
          maxDate: maxDate?.toISOString(),
          today: today.toISOString(),
          currentDestStart: currentDest?.startDate?.toISOString(),
          currentDestEnd: currentDest?.endDate?.toISOString(),
          tripStart: tripStartDate?.toISOString(),
          tripEnd: tripEndDate?.toISOString(),
          hasValidTripStart,
          hasValidTripEnd,
        });
        
        return (
          <Calendar
            visible={true}
            onClose={() => setShowCalendar(null)}
            onSelectDate={(date) => {
              if (showCalendar) {
                // Normalize the selected date
                const normalizedDate = normalizeDate(date);
                
                if (showCalendar.type === 'start') {
                  handleUpdateDestination(showCalendar.index, { startDate: normalizedDate });
                } else {
                  handleUpdateDestination(showCalendar.index, { endDate: normalizedDate });
                }
                setShowCalendar(null);
              }
            }}
            selectedDate={
              isStartDate
                ? currentDest?.startDate
                : currentDest?.endDate
            }
            minDate={minDate}
            maxDate={maxDate}
            title={`Select ${isStartDate ? 'Arrival' : 'Departure'} Date`}
          />
        );
      })()}
    </View>
  );
}

function MultiCityDestinationFallback() {
  const theme = useTheme();
  return (
    <Card variant="outlined" style={{ padding: theme.spacing(1.5) }}>
      <Caption color={theme.colors.textSecondary}>
        Multi-city destinations are temporarily unavailable. Try closing and reopening this form.
      </Caption>
    </Card>
  );
}

export function MultiCityDestinationForm(props: MultiCityDestinationFormProps) {
  return (
    <TripErrorBoundary name="MultiCityDestinationForm" fallback={<MultiCityDestinationFallback />}>
      <MultiCityDestinationFormInner {...props} />
    </TripErrorBoundary>
  );
}

